"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useGailDock } from "@/contexts/chat/GailDockContext";
import { resolveRouteFromPathname } from "@/data/routeRegistry";
import type { PageContext, PageEntityContext } from "@/services/wingman/types";

export type PageContextSupplier = () => PageEntityContext[] | null;

type PageContextValue = {
  registerPageContext: (supplier: PageContextSupplier) => () => void;
  getPageContext: () => PageContext | null;
};

const PageContextContext = createContext<PageContextValue | undefined>(undefined);

export const PageContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const suppliersRef = useRef<Set<PageContextSupplier>>(new Set());

  const { isGailGptDockEnabled } = useGailDock();

  const registerPageContext = useCallback((supplier: PageContextSupplier) => {
    suppliersRef.current.add(supplier);
    return () => {
      suppliersRef.current.delete(supplier);
    };
  }, []);

  const getPageContext = useCallback((): PageContext | null => {
    if (typeof window === "undefined") return null;
    if (!isGailGptDockEnabled) return null;

    const { pathname, search } = window.location;
    const route = resolveRouteFromPathname(pathname);

    const entities: PageEntityContext[] = [];
    for (const supplier of suppliersRef.current) {
      try {
        const supplied = supplier();
        if (supplied && supplied.length > 0) {
          entities.push(...supplied);
        }
      } catch {
        // A misbehaving page supplier must never block sending the message.
      }
    }

    return {
      url: `${pathname}${search}`,
      title: document.title || undefined,
      routeName: route?.name,
      routeDescription: route?.description,
      entities: entities.length > 0 ? entities : undefined,
    };
  }, [isGailGptDockEnabled]);

  const value = useMemo(
    () => ({ registerPageContext, getPageContext }),
    [registerPageContext, getPageContext]
  );

  return (
    <PageContextContext.Provider value={value}>
      {children}
    </PageContextContext.Provider>
  );
};

const NO_PAGE_CONTEXT: PageContextValue = {
  registerPageContext: () => () => {},
  getPageContext: () => null,
};

export const usePageContext = (): PageContextValue =>
  useContext(PageContextContext) ?? NO_PAGE_CONTEXT;

export const useRegisterPageContext = (
  supplier: PageContextSupplier
): void => {
  const { registerPageContext } = usePageContext();
  const supplierRef = useRef(supplier);
  supplierRef.current = supplier;

  useEffect(() => {
    return registerPageContext(() => supplierRef.current());
  }, [registerPageContext]);
};
